import { colorSystem } from '@luminovo/design-system';
import { Axis, AxisBottom, Orientation } from '@visx/axis';
import { curveLinear } from '@visx/curve';
import { localPoint } from '@visx/event';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { MarkerCircle } from '@visx/marker';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AreaClosed as AreaClosedContainer, LinePath } from '@visx/shape';
import { defaultStyles, Tooltip, useTooltip } from '@visx/tooltip';
import { useId, useMemo } from 'react';

export type AreaClosedProps<T extends Datum> = {
    data: T[];
    color: string;
    dotCircleColor?: string;
    showDotCircles?: boolean;
    Tooltip: ({ datum }: { datum: T }) => JSX.Element;
    valueFormatter?: (
        v: number,
        index: number,
        values: {
            value: T;
            index: number;
        }[],
    ) => string;
    onClick?: (datum: T) => void;
    showYAxis?: boolean;
    //These are the values that will be shown on the x-axis. If not provided, the min and max values of Datum will be used.
    xAxis?: number[];
    // If provided, the y-axis will be fixed to this range. If not provided, the y-axis will be scaled to the data.
    yAxisDomainRange?: [number, number];
    // If true, the tooltip and vertical line follow the mouse x position instead of snapping to data points.
    followMouseX?: boolean;
};

type Datum = {
    xAxis: number;
    yAxis: number;
};

export function AreaClosed<T extends Datum>(props: AreaClosedProps<T>): JSX.Element {
    return (
        <ParentSize>
            {({ width, height }) => {
                return <AreaClosedInner {...props} parentWidth={width} parentHeight={height} />;
            }}
        </ParentSize>
    );
}

function AreaClosedInner<T extends Datum>({
    parentWidth = 0,
    parentHeight = 0,
    Tooltip: TooltipComponent,
    data,
    color,
    valueFormatter,
    onClick,
    showYAxis = true,
    xAxis,
    dotCircleColor,
    showDotCircles = true,
    yAxisDomainRange,
    followMouseX = false,
}: AreaClosedProps<T> & { parentWidth?: number; parentHeight?: number }): JSX.Element {
    const instanceId = useId();
    const gradientId = `gradient-${instanceId}`;
    const markerId = `marker-circle-${instanceId}`;

    const { tooltipOpen, tooltipTop, tooltipLeft, showTooltip, tooltipData, hideTooltip } = useTooltip<T>();
    const getX = (d: T) => d.xAxis;
    const getY = (d: T) => d.yAxis;
    const sortedData = useMemo(() => [...data].sort((a, b) => a.xAxis - b.xAxis), [data]);

    // bounds
    const xMax = parentWidth;
    const yMax = parentHeight;
    const xScaleDomain = xAxis
        ? [xAxis[0], xAxis[xAxis.length - 1]]
        : [
              data.reduce((max, d) => Math.min(max, getX(d)), Infinity),
              data.reduce((max, d) => Math.max(max, getX(d)), 0),
          ];
    const xScale = scaleLinear({
        domain: xScaleDomain,
        range: [0, xMax],
        round: true,
    });

    const yScaleDomain = yAxisDomainRange
        ? yAxisDomainRange
        : [
              data.reduce((max, d) => Math.min(max, getY(d)), Infinity),
              data.reduce((max, d) => Math.max(max, getY(d)), 0),
          ];

    const yScale = scaleLinear({
        domain: yScaleDomain,
        range: [yMax, 0],
    });

    const tickValues = xAxis ? xAxis : undefined;
    const handleClick = () => {
        if (onClick && tooltipData) onClick(tooltipData);
    };

    const markerCircleColor = dotCircleColor ? dotCircleColor : color;

    return (
        <div style={{ position: 'relative', width: parentWidth, height: parentHeight }}>
            <svg
                onClick={handleClick}
                width={parentWidth}
                height={parentHeight}
                style={{ overflow: 'visible' }}
                onMouseMove={(e) => {
                    const point = localPoint(e);
                    const mouseX = point?.x ?? 0;
                    const inverseX = xScale.invert(mouseX);
                    const nearest = sortedData.findIndex((d) => d.xAxis >= inverseX);
                    const dataPoint = sortedData[Math.max(nearest - 1, 0)];
                    if (dataPoint) {
                        showTooltip({
                            tooltipLeft: followMouseX ? mouseX : xScale(dataPoint.xAxis),
                            tooltipTop: 0,
                            tooltipData: dataPoint,
                        });
                    }
                }}
                onMouseLeave={() => hideTooltip()}
            >
                <Group left={0} top={0}>
                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="30%" stopColor={color} stopOpacity={0.4} />
                        <stop offset="50%" stopColor={color} stopOpacity={0.2} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                    {showDotCircles && <MarkerCircle id={markerId} fill={markerCircleColor} refX="4" size={4} />}
                    {showYAxis && (
                        <>
                            <GridRows
                                width={xMax}
                                scale={yScale}
                                strokeDasharray="3,6"
                                numTicks={Math.min(data.length, 5)}
                                stroke={colorSystem.neutral[2]}
                                strokeOpacity={1}
                                pointerEvents="none"
                            />
                            <Axis
                                tickStroke="transparent"
                                orientation={Orientation.left}
                                numTicks={Math.min(data.length, 5)}
                                scale={yScale}
                                tickFormat={(v) => {
                                    const num = typeof v === 'number' ? v : Number(v);
                                    return isNaN(num) ? '' : String(num);
                                }}
                                stroke="transparent"
                                tickLabelProps={() => ({
                                    textAnchor: 'end',
                                    dy: 4,
                                    fontSize: 12,
                                    fill: colorSystem.neutral[8],
                                    fontFamily: "'INTER', sans-serif",
                                })}
                            />
                        </>
                    )}
                    <AreaClosedContainer
                        data={sortedData}
                        x={(x) => xScale(getX(x))}
                        y={(y) => yScale(getY(y))}
                        yScale={yScale}
                        fill={`url(#${gradientId})`}
                        curve={curveLinear}
                    />
                    {tooltipOpen && (
                        <>
                            <line
                                x1={tooltipLeft}
                                x2={tooltipLeft}
                                y1={0}
                                y2={parentHeight}
                                strokeDasharray="4,2"
                                stroke={color}
                                strokeWidth={1}
                            />
                        </>
                    )}
                    <LinePath
                        data={sortedData}
                        x={(x) => xScale(getX(x))}
                        y={(y) => yScale(getY(y))}
                        stroke={color}
                        strokeWidth={1}
                        curve={curveLinear}
                        markerStart={showDotCircles ? `url(#${markerId})` : undefined}
                        markerMid={showDotCircles ? `url(#${markerId})` : undefined}
                        markerEnd={showDotCircles ? `url(#${markerId})` : undefined}
                    />
                    <AxisBottom
                        top={yMax}
                        scale={xScale}
                        numTicks={Math.min(data.length, 5)}
                        stroke={colorSystem.neutral[2]}
                        tickStroke={'transparent'}
                        tickValues={tickValues}
                        //@ts-ignore
                        tickFormat={valueFormatter}
                        tickLabelProps={() => ({
                            fill: colorSystem.neutral[8],
                            fontSize: 12,
                            textAnchor: 'middle',
                            fontFamily: "'INTER', sans-serif",
                        })}
                    />
                </Group>
            </svg>
            {tooltipData && (
                <>
                    <Tooltip
                        style={toolTipStyles}
                        // set this to random so it correctly updates with parent bounds
                        // oxlint-disable-next-line react/purity
                        key={Math.random()}
                        top={tooltipTop}
                        left={tooltipLeft}
                    >
                        <TooltipComponent datum={tooltipData} />
                    </Tooltip>
                </>
            )}
        </div>
    );
}

const toolTipStyles = {
    ...defaultStyles,
    fontFamily: "'INTER', sans-serif",
};
